import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Pencil } from 'lucide-react';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { useAuth } from '../context/AuthContext';
import { useLocations } from '../lib/locations';
import { useCustomPages } from '../lib/customPages';
import SEOFooterLinks from './SEOFooterLinks';

const DAY_LABELS = {
  monday: 'Mo', tuesday: 'Di', wednesday: 'Mi', thursday: 'Do',
  friday: 'Fr', saturday: 'Sa', sunday: 'So',
};
const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const Footer = () => {
  const { getFooter, getSocial, getOpeningHours, getBusinessInfo } = useSiteSettings();
  const { isMaster } = useAuth();
  // Standorte (Master-Panel → "Standorte"): Hauptstandort, Niederlassungen,
  // Partnerbetriebe. Solange nichts geladen ist (oder die API ausfällt) zeigt
  // der Footer weiter die Hauptadresse aus den Firmendaten.
  const allLocations = useLocations();
  const footerLocations = (allLocations || []).filter((l) => l.show_in_footer);
  // Eigene Seiten aus dem Seiten-Builder, die der Master im Footer verlinkt haben möchte.
  const footerPages = useCustomPages().filter((p) => p.show_in_footer);
  const footerRaw = getFooter();
  const biz = getBusinessInfo();
  // Business-Info hat Priorität — falls dort E-Mail/Telefon/Adresse gepflegt sind,
  // gewinnen diese über die Footer-Sub-Section (Single Source of Truth).
  const footer = {
    ...footerRaw,
    phone:   biz.phone   || footerRaw.phone,
    email:   biz.email   || footerRaw.email,
    address: [biz.street, biz.zip, biz.city].filter(Boolean).join(', ') || footerRaw.address,
  };
  const social = getSocial();
  const hours = getOpeningHours();

  /* Group consecutive days with same hours for compact display */
  const getCompactHours = () => {
    if (!hours) return [];
    const groups = [];
    let current = null;
    DAY_ORDER.forEach(day => {
      const h = hours[day] || 'Geschlossen';
      if (current && current.hours === h) {
        current.end = day;
      } else {
        current = { start: day, end: day, hours: h };
        groups.push(current);
      }
    });
    return groups.map(g => ({
      label: g.start === g.end ? DAY_LABELS[g.start] : `${DAY_LABELS[g.start]}-${DAY_LABELS[g.end]}`,
      hours: g.hours,
    }));
  };

  const compactHours = getCompactHours();

  const googleReviewUrl = social.google_review_url || '';

  return (
    <footer className="bg-[#0a0909] border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <img
              src="/branding/oneworld-logo.png"
              alt="One World"
              className="h-12 mb-4"
            />
            <p className="text-[#a1a1aa] text-sm leading-relaxed">
              {footer.description}
            </p>
            <div className="mt-3 space-y-3" data-testid="footer-locations">
              {footerLocations.length > 0 ? footerLocations.map((loc) => (
                <div key={loc.id} className="text-[#a1a1aa] text-xs leading-relaxed" data-testid={`footer-location-${loc.slug}`}>
                  <p className="font-medium text-white text-sm flex flex-wrap items-center gap-x-2">
                    <Link to={`/kontakt#standort-${loc.slug}`} className="hover:text-[#0094ff] transition-colors">{loc.name}</Link>
                    {loc.kind === 'partner' && <span className="text-[10px] uppercase tracking-wide text-amber-300/90">Partnerbetrieb</span>}
                    {loc.coming_soon && loc.opening_date_de && <span className="text-[10px] uppercase tracking-wide text-emerald-300/90">Neu ab {loc.opening_date_de}</span>}
                  </p>
                  {loc.kind === 'partner' && loc.company_name && loc.company_name !== loc.name && <p>{loc.company_name}</p>}
                  {(loc.address_lines || []).map((line) => <p key={line}>{line}</p>)}
                  {loc.route_url && (
                    <a href={loc.route_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-0.5 text-[#0094ff] hover:underline">
                      <MapPin className="w-3 h-3" />Route
                    </a>
                  )}
                </div>
              )) : (
                <div className="text-[#a1a1aa] text-xs leading-relaxed">
                  <p className="font-medium text-white text-sm">Niederlassung Saar</p>
                  <p>{biz.street}</p>
                  <p>D-{biz.zip} {biz.city}, Saarland</p>
                </div>
              )}
              {isMaster && (
                <Link to="/master?tab=locations" className="inline-flex items-center gap-1 text-[11px] text-[#605e5e] hover:text-[#0094ff] transition-colors" data-testid="footer-edit-locations">
                  <Pencil className="w-3 h-3" />Standorte bearbeiten
                </Link>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-sm tracking-wider uppercase">Navigation</h3>
            <ul className="space-y-2">
              {[
                { name: 'Über uns', href: '/ueber-uns' },
                { name: 'Funktionen', href: '/funktionen' },
                { name: 'Preise & Services', href: '/services' },
                { name: 'Produkte', href: '/produkte' },
                { name: 'Werkzeuge', href: '/werkzeuge' },
                { name: 'Termin anfragen', href: '/termin' },
                { name: 'Service-Finder', href: '/konfigurator' },
                { name: 'FAQ', href: '/faq' },
                { name: 'Einzugsgebiet', href: '/einzugsgebiet' },
                { name: 'Kontakt', href: '/kontakt' },
                ...footerPages.map((p) => ({ name: p.title, href: `/seite/${p.slug}` })),
              ].map(link => (
                <li key={link.href}>
                  <Link to={link.href} className="text-[#a1a1aa] hover:text-[#0094ff] text-sm transition-colors">{link.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Opening Hours */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-sm tracking-wider uppercase">Öffnungszeiten</h3>
            <div className="space-y-1.5" data-testid="footer-opening-hours">
              {compactHours.map((row) => (
                <div key={row.label} className="flex justify-between text-sm">
                  <span className="text-[#a1a1aa]">{row.label}</span>
                  <span className={`${row.hours === 'Geschlossen' ? 'text-red-400/70' : 'text-white'} font-medium`}>{row.hours}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-sm tracking-wider uppercase">Rechtliches</h3>
            <ul className="space-y-2">
              {[
                { name: 'Impressum', href: '/impressum' },
                { name: 'Datenschutz', href: '/datenschutz' },
                { name: 'AGB', href: '/agb' },
                { name: 'Widerrufsrecht', href: '/widerruf' },
                { name: 'Fahrzeugannahme & Sicherheit', href: '/fahrzeugannahme' },
              ].map(link => (
                <li key={link.href}>
                  <Link to={link.href} className="text-[#a1a1aa] hover:text-[#0094ff] text-sm transition-colors">{link.name}</Link>
                </li>
              ))}
              <li>
                <button
                  type="button"
                  onClick={() => { try { window.dispatchEvent(new Event('consent:open')); } catch { /* noop */ } }}
                  className="text-[#a1a1aa] hover:text-[#0094ff] text-sm transition-colors"
                  data-testid="footer-cookie-settings"
                >
                  Cookie-Einstellungen
                </button>
              </li>
              <li className="pt-4 border-t border-white/5 mt-4">
                <Link to="/admin/login" className="text-[#605e5e] hover:text-[#0094ff] text-xs transition-colors" data-testid="footer-admin-login">
                  Serviceberater Login
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact & Social */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-sm tracking-wider uppercase">Kontakt</h3>
            <div className="space-y-2 text-sm text-[#a1a1aa]">
              <p>Tel: {footer.phone}</p>
              <p>Email: {footer.email}</p>
              {footerLocations.length > 1 ? (
                <p><Link to="/kontakt#standorte" className="hover:text-[#0094ff] transition-colors">Alle {footerLocations.length} Standorte &amp; Anfahrt</Link></p>
              ) : (
                <>
                  <p>{footerLocations[0]?.street || biz.street}</p>
                  <p>D-{footerLocations[0]?.zip || biz.zip} {footerLocations[0]?.city || biz.city}, Saarland</p>
                </>
              )}
            </div>
            <div className="flex items-center gap-4 mt-4">
              <a href={social.instagram || 'https://www.instagram.com/chrobe_oneworld?igsh=cWRwYTc2NTZrdzRj'} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 rounded-full bg-[#231f1f] flex items-center justify-center text-[#a1a1aa] hover:text-[#0094ff] hover:bg-[#0094ff]/10 transition-colors" data-testid="footer-instagram">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
              <a href={`https://wa.me/${social.whatsapp || '4968815959023'}`} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="w-10 h-10 rounded-full bg-[#231f1f] flex items-center justify-center text-[#a1a1aa] hover:text-[#25D366] hover:bg-[#25D366]/10 transition-colors" data-testid="footer-whatsapp">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              </a>
              {googleReviewUrl && (
                <a href={googleReviewUrl} target="_blank" rel="noopener noreferrer" aria-label="Google Bewertungen" className="w-10 h-10 rounded-full bg-[#231f1f] flex items-center justify-center text-[#a1a1aa] hover:text-[#FBBC05] hover:bg-[#FBBC05]/10 transition-colors" data-testid="footer-google-review">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                </a>
              )}
            </div>
            {googleReviewUrl && (
              <a href={googleReviewUrl} target="_blank" rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#231f1f] rounded-lg text-sm text-[#a1a1aa] hover:text-[#FBBC05] hover:bg-[#FBBC05]/10 border border-[#605e5e]/10 hover:border-[#FBBC05]/30 transition-all"
                data-testid="footer-google-review-btn">
                <svg className="w-4 h-4" fill="#FBBC05" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                Bewerte uns auf Google
              </a>
            )}
          </div>
        </div>

        {/* SEO Linkblock — dezent, einklappbar, hilft Google alle
            programmatischen Landingpages (Aufbereiter / Waschbox /
            Detailing pro Stadt) zu finden. */}
        <SEOFooterLinks />

        {/* Bottom Bar */}
        <div className="border-t border-white/5 mt-8 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[#605e5e] text-xs">&copy; {new Date().getFullYear()} {footer.copyright}</p>
          <p className="text-[#605e5e] text-xs">Powered by One World</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
